"use client";

import React, { useState } from "react";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { sendEmail } from "@/app/actions/sendEmail";
import bearImg from "@/app/assets/illustration/7 contact/baby bear.webp";

import FormSuccessMessage from "./formSuccessMessage";

interface FormData {
    email: string;
    name: string;
    question: string;
}

interface ContactSectionProps {
    variant?: "contact" | "feedback";
}

const ContactSection = ({ variant = "contact" }: ContactSectionProps) => {
    const t = useTranslations("NewHome.contact");
    const [formData, setFormData] = useState<FormData>({
        email: "",
        name: "",
        question: "",
    });
    const [status, setStatus] = useState<"error" | "idle" | "loading" | "success">("idle");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus("loading");

        try {
            const result = await sendEmail({ ...formData, variant });

            if (!result.success) {
                throw new Error(result.error);
            }

            setStatus("success");
        } catch (error) {
            console.error("Form submission error:", error);
            setStatus("error");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    return (
        <section className="bg-light-100 pt-12 pb-24" id="contact-section">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start max-w-7xl mx-auto">
                    <div className="text-left w-full">
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">{t(`${variant}.title`)}</h2>
                        <h2 className="text-3xl md:text-4xl font-bold text-black mb-12">{t(`${variant}.title2`)}</h2>
                        <div className="relative w-full aspect-square max-w-40 ml-0">
                            <Image
                                alt="Baby bear illustration"
                                className="object-contain"
                                fill
                                priority
                                src={bearImg}
                            />
                        </div>
                    </div>
                    <div>
                        {status === "success" ? (
                            <FormSuccessMessage variant={variant} />
                        ) : (
                            <form className="space-y-10" onSubmit={handleSubmit}>
                                <div className="space-y-2">
                                    <label className="block text-black text-lg" htmlFor="name">
                                        {t("form.name.label")}
                                    </label>
                                    <input
                                        className="w-full text-xl py-1 bg-transparent border-b border-black focus:border-primary-100 focus:outline-none transition-colors placeholder:text-primary-300 text-black"
                                        id="name"
                                        name="name"
                                        onChange={handleChange}
                                        placeholder={t("form.name.placeholder")}
                                        required
                                        type="text"
                                        value={formData.name}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-black text-lg" htmlFor="email">
                                        {t("form.email.label")}
                                    </label>
                                    <input
                                        className="w-full text-xl py-1 bg-transparent border-b border-black focus:border-primary-100 focus:outline-none transition-colors placeholder:text-primary-300 text-black"
                                        id="email"
                                        name="email"
                                        onChange={handleChange}
                                        placeholder={t("form.email.placeholder")}
                                        required
                                        type="email"
                                        value={formData.email}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-black text-lg" htmlFor="question">
                                        {t(`${variant}.form.question.label`)}
                                    </label>
                                    <textarea
                                        className="w-full text-xl py-1 bg-transparent border-b border-black focus:border-primary-100 focus:outline-none transition-colors placeholder:text-primary-300 text-black min-h-[100px] resize-none"
                                        id="question"
                                        name="question"
                                        onChange={handleChange}
                                        placeholder={t(`${variant}.form.question.placeholder`)}
                                        required
                                        value={formData.question}
                                    />
                                </div>
                                <div className="flex justify-center md:justify-start">
                                    <button
                                        className={`px-8 py-3 bg-primary text-white rounded-full text-lg font-medium transition-opacity ${
                                            status === "loading" ? "opacity-50" : "hover:opacity-80"
                                        }`}
                                        disabled={status === "loading"}
                                        type="submit"
                                    >
                                        {status === "loading" ? t("form.sending") : t("form.submit")}
                                    </button>
                                </div>
                                {status === "error" && (
                                    <div className="text-lg text-center md:text-left text-red-600">
                                        {t("form.error")}
                                    </div>
                                )}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactSection;
